import React, { useCallback, useRef, useState } from 'react';
import ringArt from '../../../assets/img/portal-ring.png';

/**
 * Where in the cover frame the ring should look.
 *
 * The frame you pick on the COVER step is 9:16, and the ring's face is a circle — so something
 * has to be cut. Left alone that's the middle of the frame, which is rarely where the person is.
 * Drag the picture around inside the circle and the part you leave showing is the part that
 * lands on everyone's rail.
 *
 * The focal point is stored as `object-position` percentages, so the same two numbers work on
 * every size the ring is ever drawn at — 46px in the navbar, 72px on a desktop rail — without
 * re-cropping anything.
 */
const CIRCLE = 240;

export default function CoverFocusEditor({ cover, focus, onChange, onClose }) {
  const [local, setLocal] = useState(focus || { x: 50, y: 50 });
  const drag = useRef(null);

  const onPointerDown = (e) => {
    // Record the grab FIRST. Capturing the pointer only keeps the drag alive past the edge of
    // the circle — it can throw on an id the browser doesn't know, and a failed optimisation
    // must not cost you the drag itself.
    drag.current = { startX: e.clientX, startY: e.clientY, from: local };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* drag still works */ }
  };

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    // Drag right, the picture moves right — which means looking further LEFT in the frame.
    const dx = ((e.clientX - d.startX) / CIRCLE) * 100;
    const dy = ((e.clientY - d.startY) / CIRCLE) * 100;
    setLocal({
      x: Math.max(0, Math.min(100, d.from.x - dx)),
      y: Math.max(0, Math.min(100, d.from.y - dy)),
    });
  }, []);

  const onPointerUp = () => { drag.current = null; };

  const done = () => { onChange(local); onClose(); };

  return (
    <div className="fixed inset-0 safe-inset z-[110] flex flex-col items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <p className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.10em', color: '#9A9A9A' }}>
        Position it in the ring
      </p>
      <p className="mt-2 max-w-xs text-center text-white/70" style={{ fontSize: 13, lineHeight: 1.5 }}>
        Drag the picture. What you leave showing is what lands on everyone&apos;s rail.
      </p>

      <div
        className="relative mt-6 touch-none select-none"
        style={{ width: CIRCLE, height: CIRCLE, cursor: 'grab' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="application"
        aria-label="Drag to position the cover in the ring"
      >
        <span className="pr-portal-ring absolute inset-0" style={{ backgroundImage: `url(${ringArt})` }} aria-hidden />
        <span className="absolute overflow-hidden rounded-full bg-black" style={{ inset: '5.5%' }}>
          {cover && (
            <img
              src={cover}
              alt=""
              draggable={false}
              className="h-full w-full"
              style={{ objectFit: 'cover', objectPosition: `${local.x}% ${local.y}%` }}
            />
          )}
        </span>
      </div>

      <div className="mt-8 flex w-full max-w-xs flex-col" style={{ gap: 8 }}>
        <button
          type="button"
          onClick={done}
          className="pr-story-action w-full font-mono font-semibold uppercase"
          style={{ background: '#FFFFFF', color: '#000000', border: '2px solid #FFFFFF', fontSize: 14, letterSpacing: '0.05em', padding: '14px 16px', transition: 'background-color 200ms, color 200ms' }}
        >
          Use this
        </button>
        <button
          type="button"
          onClick={() => setLocal({ x: 50, y: 50 })}
          className="w-full font-mono font-bold uppercase text-white/60 hover:text-white"
          style={{ fontSize: 11, letterSpacing: '0.10em', padding: '8px 0', transition: 'color 200ms' }}
        >
          Centre it
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full font-mono font-bold uppercase text-white/60 hover:text-white"
          style={{ fontSize: 11, letterSpacing: '0.10em', padding: '8px 0', transition: 'color 200ms' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
